import { GridSchema } from "components/GridView/GridView.types";
import Grid from "components/GridView/components/Grid";
import IconButton from "components/blocks/IconButton";
import LoadingOverlay from "components/blocks/LoadingOverlay";
import Modal from "components/blocks/Modal";
import AttachmentCard from "components/blocks/AttachmentCard";
import { useCookieContext } from "contexts";
import { useQuery } from "hooks/useQuery";
import useSystemDateTheme from "hooks/useSystemDateTheme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CourseMinutesService } from "services/courseMinutesService";
import { CourseOrderMinutesResult } from "services/courseMinutesService/Model/CourseSpeechesTypes";
import { FileResult } from "services/shared-models/sharedTypes";

const PreviousRecords = ({
  courseOrderId,
  shouldUpdateTable,
}: {
  courseOrderId: number;
  shouldUpdateTable: boolean;
}) => {
  const { t } = useTranslation("Orders");
  const { toSystemThemeDateFormat } = useSystemDateTheme();
  const { isAr } = useCookieContext();

  const [filesModal, setFilesModal] = useState<{
    isOpen: boolean;
    files: FileResult[];
  }>({ isOpen: false, files: [] });

  const { data, loading } = useQuery({
    queryFn: async () => {
      return await CourseMinutesService.getAllCourseMinutesByCourseOrderId(courseOrderId);
    },
    triggers: [shouldUpdateTable, isAr],
  });

  const previousRecordTableHeaders = useMemo<GridSchema[]>(() => {
    return [
      {
        accessor: "courseMinutesNumber",
        field: "courseMinutesNumber",
        displayName: t("recordNumber"),
        showOnMobile: true,
        render: (row: CourseOrderMinutesResult) => <>{row.courseMinutesNumber}</>,
      },
      {
        accessor: "date",
        field: "date",
        displayName: t("date"),
        render: (row: CourseOrderMinutesResult) => <>{toSystemThemeDateFormat(row.date)}</>,
      },
      {
        accessor: "members",
        field: "members",
        displayName: t("members"),
        render: (row: CourseOrderMinutesResult) => <>{row.users.join(", ")}</>,
      },
      {
        accessor: "description",
        field: "description",
        displayName: t("recordDetails"),
      },
      {
        accessor: "files",
        field: "files",
        displayName: t("files"),
        showOnMobile: true,
        render(row: CourseOrderMinutesResult) {
          return !!row.files.length ? (
            <div className="d-flex ">
              <IconButton
                icon="icon-view"
                variant="transparent"
                innerIconColor="primary"
                onClick={() => {
                  setFilesModal({ isOpen: true, files: row.files });
                }}
              />
            </div>
          ) : (
            <></>
          );
        },
      },
    ];
  }, [t, toSystemThemeDateFormat, setFilesModal]);

  return (
    <>
      <div className="position-relative">
        <LoadingOverlay visible={loading} />
        <Grid gridSchema={previousRecordTableHeaders} data={data?.result ?? []} />
      </div>

      <Modal
        containerClassName="bg-white pb-2"
        isOpen={filesModal.isOpen}
        onClose={() => setFilesModal({ isOpen: false, files: [] })}
        title={t("Common:attachments")}
        withHeaderBorder
        size="lg"
        titleClassName="fs-6 text-dark"
      >
        <div className="d-flex gap-2 flex-wrap">
          {filesModal.files && filesModal.files.length > 0 ? (
            filesModal.files.map((file: FileResult, index: number) => (
              <AttachmentCard
                key={`${file.id ?? index}`}
                attachment={{
                  extension: file.extension ?? "",
                  fileName: file.name ?? "",
                  id: file.id ?? "",
                  size: file.size ?? "",
                }}
              />
            ))
          ) : (
            <p className="text-muted">{t("Common:noAttachments")}</p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default PreviousRecords;
